/**
 * WinnerScreen - Displays the match winner and game-over state.
 *
 * Responsibilities:
 * - Display match winner when game over is received
 * - Show final standings for all players
 * - Indicate whether the current player won or lost
 * - React to GameOver messages from the authoritative backend
 * - Provide render data for UI binding
 * - Reset when a new match starts
 *
 * The backend is authoritative; this component only renders game-over data
 * that the server sends via GameOver messages.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import { GameOver, PlayerData } from '../../shared/messages/MessageContracts';
import { eliminationOrderKey } from '../../shared/models/Player';

// ─── Winner Render Data ─────────────────────────────────────────────────────

/**
 * Final standing for a player in the match.
 */
export interface PlayerStanding {
  /** Player ID. */
  playerId: string;
  /**
   * Display name override (COOP mission bots carry their campaign
   * character name — the authoritative PlayerData.displayName). null for
   * everyone else; the client renders the id when it is null.
   */
  displayName?: string | null;
  /** Placement rank (1-based, 1 = winner). */
  placement: number;
  /** Whether this is the current player. */
  isCurrentPlayer: boolean;
  /** Whether this player is the winner. */
  isWinner: boolean;
  /** Final life value. */
  finalLife: number;
  /** Final gold value. */
  finalGold: number;
}

/**
 * Render data for the winner screen.
 */
export interface WinnerScreenRenderData {
  /** Whether the winner screen should be visible (game over received). */
  isVisible: boolean;

  /** Whether the game is over. */
  isGameOver: boolean;

  /** Whether the current player won the match. */
  isWinner: boolean;

  /** Winner player ID. */
  winnerId: string | null;

  /** Match ID. */
  matchId: string | null;

  /** Final standings sorted by placement. */
  standings: PlayerStanding[];

  /** Total number of players in the match. */
  totalPlayers: number;

  /** Current player's placement (1-based). */
  currentPlayerPlacement: number | null;

  /** Human-readable victory/defeat message. */
  victoryMessage: string;

  /** Whether player-action controls should be hidden (always true when game over). */
  hidePlayerControls: boolean;

  /** The raw GameOver message from the server. */
  rawGameOver: GameOver | null;
}

/**
 * Callback invoked when render data changes.
 */
export type WinnerScreenRenderCallback = (data: WinnerScreenRenderData) => void;

/**
 * Callback invoked when the game is over.
 */
export type GameOverCallback = (gameOver: GameOver) => void;

// ─── Winner Screen ──────────────────────────────────────────────────────────

export class WinnerScreen {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: WinnerScreenRenderCallback[] = [];
  private _gameOverCallbacks: GameOverCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;

  // Internal state
  private _gameOver: GameOver | null = null;

  constructor(appShell: AppShell) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data. */
  get renderData(): WinnerScreenRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the winner screen is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  /** Returns true if the game is over. */
  get isGameOver(): boolean {
    return this._buildRenderData().isGameOver;
  }

  /** Returns true if the current player won the match. */
  get isWinner(): boolean {
    return this._buildRenderData().isWinner;
  }

  /** Returns the winner player ID, or null if game is not over. */
  get winnerId(): string | null {
    return this._buildRenderData().winnerId;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the winner screen and start listening for game-over events.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    this._notifyRenderCallbacks();
  }

  /**
   * Hide the winner screen and stop listening for events.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  /**
   * Reset the winner screen state (called when a new match starts).
   */
  reset(): void {
    this._gameOver = null;
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: WinnerScreenRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to game-over events.
   * Returns an unsubscribe function.
   */
  onGameOver(callback: GameOverCallback): () => void {
    this._gameOverCallbacks.push(callback);
    return () => {
      const index = this._gameOverCallbacks.indexOf(callback);
      if (index !== -1) {
        this._gameOverCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Manual GameOver Setting ─────────────────────────────────────────────

  /**
   * Manually set the game-over state (for testing or direct API use).
   */
  setGameOver(gameOver: GameOver): void {
    this._gameOver = gameOver;
    this._notifyGameOverCallbacks(gameOver);
    this._notifyRenderCallbacks();
  }

  // ─── Internal Event Handling ─────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        const state = event.data as { match: any };
        const matchState = state.match;

        // Handle game over from match state
        if (matchState.gameOver && matchState.winnerId) {
          // Build GameOver from match state if not already set
          if (!this._gameOver) {
            this._gameOver = {
              version: '1.0.0',
              messageType: 'GameOver',
              matchId: matchState.matchId,
              winnerId: matchState.winnerId,
              finalPlayers: matchState.players,
            } as GameOver;
            this._notifyGameOverCallbacks(this._gameOver);
          }
        }

        // Reset when a new match starts
        if (matchState.matchId === null || (matchState.phase === 'preparation' && !matchState.gameOver)) {
          // Only reset if there was a previous game over and we're in a fresh match
          if (this._gameOver && matchState.matchId !== this._gameOver.matchId) {
            this.reset();
          }
        }

        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        // Hide when navigating away from gameOver screen
        if (data.currentScreen !== 'gameOver' && data.currentScreen !== 'match' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }

  // ─── Internal ────────────────────────────────────────────────────────────

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _notifyGameOverCallbacks(gameOver: GameOver): void {
    for (const callback of this._gameOverCallbacks) {
      callback(gameOver);
    }
  }

  private _buildRenderData(): WinnerScreenRenderData {
    const matchState = this._appShell.gameState.match;
    const gameOver = this._gameOver;

    if (!gameOver) {
      return this._buildEmptyRenderData();
    }

    const winnerId = gameOver.winnerId;
    // COOP: the team wins together — the canonical winner is the host, so
    // a teammate viewing a team victory is a winner too.
    const coop = this._appShell.gameState.match.coop;
    const teamWon = !!coop && coop.mode !== 'normal' && winnerId !== null &&
      (winnerId === coop.hostId || winnerId === coop.teammateId);
    const isWinner = winnerId === this._playerId || teamWon;
    const finalPlayers = gameOver.finalPlayers;
    const totalPlayers = finalPlayers.length;

    // Build standings ranked by ORDER OF DEATH (authoritative) for placement
    const standings = this._computeStandings(finalPlayers, winnerId);

    // Find current player's placement
    const currentStanding = standings.find((s) => s.isCurrentPlayer);
    const currentPlayerPlacement = currentStanding ? currentStanding.placement : null;

    // Build victory message
    const victoryMessage = this._buildVictoryMessage(
      isWinner,
      winnerId,
      currentPlayerPlacement,
      totalPlayers
    );

    return {
      isVisible: true,
      isGameOver: true,
      isWinner,
      winnerId,
      matchId: gameOver.matchId,
      standings,
      totalPlayers,
      currentPlayerPlacement,
      victoryMessage,
      hidePlayerControls: true,
      rawGameOver: gameOver,
    };
  }

  private _buildEmptyRenderData(): WinnerScreenRenderData {
    return {
      isVisible: false,
      isGameOver: false,
      isWinner: false,
      winnerId: null,
      matchId: null,
      standings: [],
      totalPlayers: 0,
      currentPlayerPlacement: null,
      victoryMessage: '',
      hidePlayerControls: false,
      rawGameOver: null,
    };
  }

  private _computeStandings(finalPlayers: PlayerData[], winnerId: string): PlayerStanding[] {
    // TASK 199: when the server assigned the FINAL PLACEMENTS (present on
    // every PlayerData after match end), they are AUTHORITATIVE — purely
    // survival-based with the RANDOM tie-break among same-turn deaths
    // (unique 1..N placements, no shared ranks). Render them directly.
    if (finalPlayers.length > 0 && finalPlayers.every((p) => typeof p.finalPlacement === 'number')) {
      const sorted = [...finalPlayers].sort((a, b) => (a.finalPlacement ?? 0) - (b.finalPlacement ?? 0));
      return sorted.map((player) => ({
        playerId: player.id,
        displayName: player.displayName ?? null,
        placement: player.finalPlacement as number,
        isCurrentPlayer: player.id === this._playerId,
        isWinner: player.id === winnerId,
        finalLife: player.life,
        finalGold: player.gold,
      }));
    }

    // LEGACY fallback (protocol backward compatibility — pre-task-199
    // servers without finalPlacement): rank by ORDER OF DEATH — the player
    // who survived longer is ALWAYS placed above the one who died earlier
    // — never by remaining life or gold (every dead player has life 0, so
    // the old life→gold sort ranked the dead by WEALTH — the reported
    // bug). The shared eliminationOrderKey (set by the RoundCoordinator at
    // the moment of death) is the same order function the eliminated
    // screen uses. Equal keys TIE — standard competition ranking (shared
    // placement number, later ranks skip). The sort is stable, so tied
    // players keep their broadcast order.
    const sorted = [...finalPlayers].sort((a, b) => {
      const keyA = eliminationOrderKey(a);
      const keyB = eliminationOrderKey(b);
      if (keyA === keyB) return 0; // also keeps Infinity vs Infinity stable
      return keyB > keyA ? 1 : -1;
    });

    let lastKey: number | null = null;
    let lastPlacement = 0;
    return sorted.map((player, index) => {
      const key = eliminationOrderKey(player);
      if (key !== lastKey) {
        lastKey = key;
        lastPlacement = index + 1;
      }
      return {
        playerId: player.id,
        displayName: player.displayName ?? null,
        placement: lastPlacement,
        isCurrentPlayer: player.id === this._playerId,
        isWinner: player.id === winnerId,
        finalLife: player.life,
        finalGold: player.gold,
      };
    });
  }

  private _buildVictoryMessage(
    isWinner: boolean,
    winnerId: string | null,
    currentPlayerPlacement: number | null,
    totalPlayers: number,
  ): string {
    if (isWinner) {
      return `You won the match! You placed 1st out of ${totalPlayers} players.`;
    }

    const placementText = currentPlayerPlacement !== null
      ? `You placed ${this._getOrdinal(currentPlayerPlacement)} out of ${totalPlayers} players.`
      : 'The match has ended.';

    const winnerText = winnerId ? ` ${winnerId} won the match.` : '';
    return `${placementText}${winnerText}`;
  }

  private _getOrdinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
  }
}
